"use client";

import React from "react";
import { Loader2 } from "lucide-react";

interface CustomButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:
    | "primary"
    | "secondary"
    | "danger"
    | "success"
    | "blue"
    | "ghost"
    | "outline"
    | "red";
  size?: "xs" | "sm" | "md" | "lg";
  icon?: React.ReactNode;
  iconPosition?: "left" | "right";
  loading?: boolean;
  fullWidth?: boolean;
  isSquare?: boolean;
}

export const CustomButton: React.FC<CustomButtonProps> = ({
  variant = "primary",
  size = "md",
  icon,
  iconPosition = "left",
  loading = false,
  fullWidth = false,
  isSquare = false,
  children,
  className = "",
  disabled,
  ...props
}) => {
  const baseStyles =
    "inline-flex items-center justify-center font-bold uppercase tracking-widest transition-all rounded-lg outline-none select-none disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none";

  const variants = {
    primary:
      "bg-gradient-to-r from-gold-dim via-gold-core to-gold-glow hover:to-gold-bright text-white shadow-md hover:shadow-lg hover:-translate-y-0.5 disabled:from-black-2 disabled:via-black-2 disabled:to-black-2 disabled:text-text-3 disabled:border-border-1 disabled:border",
    secondary:
      "bg-black-2 text-text-2 hover:bg-black-3 border border-border-1 disabled:bg-black-2 disabled:text-text-3 disabled:border-border-1",
    danger:
      "border border-red-200 text-red-600 hover:bg-red-500 hover:text-white hover:border-red-500/30 disabled:border-border-1 disabled:text-text-3 disabled:bg-black-2",
    red: "bg-red-500 text-white hover:bg-red-600 shadow-md disabled:bg-black-2 disabled:text-text-3",
    success:
      "border border-green-200 text-green-600 hover:bg-green-500 hover:text-white hover:border-green-500/30 disabled:border-border-1 disabled:text-text-3 disabled:bg-black-2",
    blue: "border border-blue-200 text-blue-600 hover:bg-blue-500 hover:text-white hover:border-blue-500/30 disabled:border-border-1 disabled:text-text-3 disabled:bg-black-2",
    ghost:
      "text-text-3 hover:bg-black-2 disabled:hover:bg-transparent disabled:text-text-3",
    outline:
      "border border-border-1 text-text-2 hover:bg-black-2 disabled:text-text-3 disabled:border-border-1 disabled:bg-black-2",
  };

  const sizes = {
    xs: isSquare ? "w-6 h-6 text-[9px]" : "px-2 py-1 text-[9px] gap-1",
    sm: isSquare ? "w-7 h-7 text-[10px]" : "px-3 py-1.5 text-[10px] gap-1.5",
    md: isSquare ? "w-9 h-9 text-[10px]" : "px-5 py-2.5 text-[10px] gap-2",
    lg: isSquare
      ? "w-11 h-11 text-[11px] rounded-xl"
      : "px-6 py-3 text-[11px] gap-2.5 rounded-xl",
  };

  const widthStyle = fullWidth ? "w-full" : "";
  const squareStyles = isSquare ? "p-0 flex-shrink-0 aspect-square" : "";

  return (
    <button
      className={`cursor-pointer ${baseStyles} ${variants[variant]} ${sizes[size]} ${widthStyle} ${squareStyles} ${className}`}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <Loader2 size={size === "xs" ? 10 : 14} className="animate-spin" />
      )}
      {!loading && icon && iconPosition === "left" && (
        <span className="flex-shrink-0">{icon}</span>
      )}
      {children && <span>{children}</span>}
      {!loading && icon && iconPosition === "right" && (
        <span className="flex-shrink-0">{icon}</span>
      )}
    </button>
  );
};
